import React, { useEffect, useState } from "react";
import {
  Button,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Switch,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CreateCouponModal from "./components/coupon/CreateCouponModal";
import { SearchOutlined } from "@mui/icons-material";
import { useAppServices } from "hook/services";
import { useAgencyInfo } from "context/agency";
import Loader from "examples/Loader";
import { dateFormat } from "utils/dateFormat";
import CouponEditMenu from "./components/coupon/CouponEditMenu";
import CircularProgress from "@mui/material/CircularProgress";
import DeleteCouponModal from "./components/coupon/DeleteCouponModal";
import { useNavigate, useParams } from "react-router-dom";

export default function Coupons({ products }) {
  const { encrypted } = useParams();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("All");
  const service = useAppServices();
  const [agency] = useAgencyInfo();
  const [couponsData, setCouponsData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editCoupon, setEditCoupon] = useState(null);
  const [deleteCoupon, setDeleteCoupon] = useState(false)
  const [loadingtoggle, setLoadingToggle] = useState(false)

  const handleOpen = () => {
    setEditMode(false);
    setEditCoupon(null);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setEditMode(false);
    setEditCoupon(null);
  };

  const handleEditCoupon = (coupon) => {
    setEditMode(true);
    setEditCoupon(coupon);
    setOpen(true);
  };

  const fetchCouponsData = async (nextPage = 1) => {
    if (!agency?._id) return;
    setLoader(true);
    const { response } = await service.coupons.getCoupons({
      query: `page=${nextPage}&limit=${limit}&agency_id=${agency._id}`,
    });
    if (response) {
      setCouponsData(response);
      setPage(Number(response.page) || Number(nextPage) || 1);
      setTotalPages(Number(response.totalPages) || 1);
    }
    setLoader(false);
  };

  useEffect(() => {
    if (agency) fetchCouponsData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agency]);

  const handleDelete = (coupon) => {
    setEditCoupon(coupon);
    setDeleteCoupon(true);
  }

  const handleStatusChange = async (promo) => {
    if (!agency?._id) return;
    setLoadingToggle(promo.id);
    const { response } = await service.coupons.toggleCouponStatus({
      payload: {
        agency_id: agency._id,
        promoCodeId: promo.id,
        active: !promo.active,
      },
      toaster: true,
    });
    setLoadingToggle(false);
    if (response) {
      setCouponsData((prev) => ({
        ...prev,
        data: prev.data.map((item) =>
          item.id === promo.id ? { ...item, active: !promo.active } : item
        ),
      }));
    }
  };

  const handleViewHistory = (promo) => {
    navigate(`/${encrypted}/saas/coupons/${promo.id}/history`);
  };

  if (loader) return <Loader />;

  return (
    <div className="coupons-container">
      {/* Header */}
      <div className="coupons-header">
        <h2>Coupons</h2>
        <button
          style={{
            background: "black",
            color: "#fff",
            padding: "10px 28px",
            display: "flex",
            alignItems: "center",
            borderRadius: "8px",
            cursor: "pointer",
            fontWeight: "semibold",
          }}
          onClick={handleOpen}
        >
          <span className="plus-icon">+</span> Create Coupon
        </button>
      </div>
      <p className="subtitle">Manage coupon discounts to increase conversion</p>

      {/* Filters */}
      <div className="filters">
        {["All", "Active", "Inactive", "Expired"].map((tab) => (
          <button
            key={tab}
            style={{
              background: tab === activeTab ? "#e9e9e9" : "transparent",
              color: "black",
              border: "none",
              padding: "8px",
              borderRadius: "4px",
              cursor: "pointer",
            }}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Search */}
      <div style={{ display: "flex", justifyContent: "end" }}>
        <div className="search-container">
          <SearchOutlined style={{ color: "#a5a5a5", marginRight: 3 }} />
          <input
            type="text"
            placeholder="Search"
            style={{ background: "transparent", border: "none", outline: "none" }}
          />
        </div>
      </div>

      {/* Table */}
      <Table
        sx={{
          border: "1px solid #d9d9d9",
          marginTop: "16px",
        }}
      >
        <TableHead sx={{ width: "100%" }}>
          <TableRow sx={{ borderRadius: "8px 8px 0px 0px" }}>
            <TableCell style={{ width: "15%" }}>Coupon Name</TableCell>
            <TableCell style={{ width: "15%" }}>Coupon Code</TableCell>
            <TableCell style={{ width: "10%" }}>Discount</TableCell>
            <TableCell style={{ width: "20%" }}>Start Date</TableCell>
            <TableCell style={{ width: "20%" }}>End Date</TableCell>
            <TableCell style={{ width: "10%" }}>Status</TableCell>
            <TableCell style={{ width: "15%" }}>Redemption Count</TableCell>
            <TableCell style={{ width: "15%" }}>Enable Coupon</TableCell>
            <TableCell style={{ width: "15%" }} align="right">
              Actions
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {couponsData?.data?.length > 0 ? (
            couponsData?.data
              ?.filter((item) => {
                if (activeTab === "All") return true;
                const expiryDate = item.expires_at || item.coupon?.redeem_by;
                const isExpired = expiryDate && expiryDate > 0 && new Date(expiryDate * 1000) < new Date();
                const isScheduled = item.active && item.coupon.created && new Date(item.coupon.created * 1000) > new Date(); // Though created is usually past

                if (activeTab === "Active") return item.active && !isExpired;
                if (activeTab === "Expired") return isExpired;
                if (activeTab === "Inactive") return !item.active && !isExpired;
                return true;
              })
              ?.map((promo, idx) => (
                <TableRow key={idx}>
                  <TableCell style={{ width: "15%", color: "#000" }}>{promo.coupon.name}</TableCell>
                  <TableCell style={{ width: "15%", color: "#898989" }}>{promo.code}</TableCell>
                  <TableCell style={{ width: "10%", color: "#494949" }}>
                    {promo.coupon?.percent_off
                      ? `${parseInt(promo.coupon?.percent_off).toFixed(2)}%`
                      : `${parseInt(promo.coupon?.amount_off) / 100}`}
                  </TableCell>
                  <TableCell style={{ width: "20%", color: "#898989" }}>
                    {dateFormat(promo.coupon.created)}
                  </TableCell>
                  <TableCell style={{ width: "20%", color: "#898989" }}>
                    {promo.expires_at && promo.expires_at > 0 ? dateFormat(promo.expires_at) : ""}
                  </TableCell>
                  <TableCell style={{ width: "10%" }}>
                    {(() => {
                      const expiryDate = promo.expires_at || promo.coupon?.redeem_by;
                      const isExpired = expiryDate && expiryDate > 0 && new Date(expiryDate * 1000) < new Date();
                      const label = isExpired ? "Expired" : promo.active ? "Active" : "Inactive";
                      let background = "#FFF1C2";
                      let color = "#E5A000";

                      if (isExpired) {
                        background = "#f6a9a9ff";
                        color = "#da0000ff";
                      } else if (promo.active) {
                        background = "#eaf6ee";
                        color = "#67ac5c";
                      }

                      return (
                        <Chip
                          label={label}
                          sx={{
                            background: background,
                            color: color,
                            fontWeight: "bold",
                            border: `1px solid ${isExpired ? "#f19494ff" : promo.active ? "#cae7d6" : "#f1e3b5"}`,
                          }}
                          size="small"
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell style={{ width: "15%", color: "#000" }}>
                    {promo.coupon.times_redeemed}
                  </TableCell>
                  <TableCell style={{ width: "15%", color: "#000" }}>
                    <Switch
                      checked={promo.active}
                      onChange={() => handleStatusChange(promo)}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#000 !important",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                          backgroundColor: "#000 !important",
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "#000 !important",
                          },
                        },
                      }}
                    />
                    {loadingtoggle === promo.id && <CircularProgress size={16} sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell style={{ width: "15%" }} align="right">
                    <IconButton
                      onClick={(e) => {
                        setMenuAnchorEl(e.currentTarget);
                        setSelectedCoupon(promo);
                      }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
          ) : (
            <TableRow>
              <TableCell style={{ width: "100%", color: "#000", textAlign: "center" }}>
                No Data Found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Pagination */}
      <div className="pagination" style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 16 }}>
        <Button
          disabled={Number(page) <= 1}
          onClick={() => fetchCouponsData(Math.max(1, Number(page) - 1))}
          variant=""
          size="small"
        >
          Previous
        </Button>
        <span>
          Page {Number(page)} of {Number(totalPages)}
        </span>
        <Button
          disabled={Number(page) >= Number(totalPages)}
          onClick={() => fetchCouponsData(Number(page) + 1)}
          variant=""
          size="small"
        >
          Next
        </Button>
        <span>{limit} / page</span>
      </div>

      {/* Create Coupon Modal */}
      <CreateCouponModal
        open={open}
        onClose={handleClose}
        products={products}
        refetchCoupons={() => fetchCouponsData(page)}
        editMode={editMode}
        editData={editCoupon}
      />

      {/* Coupon Edit Menu */}
      {selectedCoupon && (
        <CouponEditMenu
          selectedCoupon={selectedCoupon}
          anchorEl={menuAnchorEl}
          onClose={() => {
            setMenuAnchorEl(null);
            setSelectedCoupon(null);
          }}
          refetchCoupons={() => fetchCouponsData(page)}
          onEdit={handleEditCoupon}
          handleDelete={handleDelete}
          handleViewHistory={handleViewHistory}
        />
      )}
      <DeleteCouponModal onClose={() => setDeleteCoupon(false)} open={deleteCoupon} refetchCoupons={() => fetchCouponsData(page)} editCoupon={editCoupon} setEditCoupon={setEditCoupon} />
    </div>
  );
}
